'use client'

import {
  Opacity,
  SquareOutline,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
} from '@carbon/icons-react'
import { LinkIcon, LinkSlashIcon, TrashIcon } from '@heroicons/react/20/solid'
import type { Node, NodeProps } from '@xyflow/react'
import { NodeResizer, NodeToolbar, Position, useReactFlow } from '@xyflow/react'
import { Button } from 'app/_components/Button'
import { highContrastText } from 'lib/color'
import type { CSSProperties, ElementType } from 'react'
import {
  createElement,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useReadOnly } from 'slate-react'
import { twMerge } from 'tailwind-merge'
import { DEFAULT_BORDER_COLOR, DEFAULT_NODE_BACKGROUND } from '../constants'
import { useUpdateNodeData } from '../hooks/useUpdateElementData'
import { ColorSelect } from './ColorSelect'
import { FlowToolbar } from './FlowToolbar'
import { NodeHandles } from './NodeHandles'

type TextAlignOption = {
  label: string
  value: CSSProperties['textAlign']
  icon: ElementType
}

const TEXT_ALIGN = {
  center: {
    label: 'zentriert',
    value: 'center',
    icon: TextAlignCenter,
  },
  left: {
    label: 'linksbündig',
    value: 'left',
    icon: TextAlignLeft,
  },
  right: {
    label: 'rechtsbündig',
    value: 'right',
    icon: TextAlignRight,
  },
} satisfies Record<string, TextAlignOption>

export type CustomNodeType = Node<{
  label: string
  backgroundColor?: string
  borderColor?: string
  linkUrl?: string | null
  textAlign?: keyof typeof TEXT_ALIGN
}>

type CustomNodeData = CustomNodeType['data']

function CustomNodeToolbar({
  data,
  isVisible,
  updateNodeData,
  onDelete,
  onToggleLink,
}: {
  data: CustomNodeData
  isVisible: boolean
  updateNodeData: (newData: Partial<CustomNodeData>) => void
  onDelete: () => void
  onToggleLink: () => void
}) {
  const textAlign = TEXT_ALIGN[data.textAlign || 'center']
  const hasDefaultBorder = data.borderColor === DEFAULT_BORDER_COLOR

  function toggleBorderTransparency() {
    updateNodeData({
      borderColor: hasDefaultBorder ? 'transparent' : DEFAULT_BORDER_COLOR,
    })
  }

  function cycleTextAlign() {
    const nextValue =
      data.textAlign === 'right'
        ? 'center'
        : data.textAlign === 'left'
          ? 'right'
          : 'left'

    updateNodeData({ textAlign: nextValue })
  }

  return (
    <NodeToolbar isVisible={isVisible} position={Position.Top}>
      <FlowToolbar>
        <ColorSelect
          value={data.backgroundColor}
          onChange={(value) => updateNodeData({ backgroundColor: value })}
        />

        <Button
          title={hasDefaultBorder ? 'Transparent machen' : 'Farbig machen'}
          shape="icon"
          variant="ghost"
          type="button"
          aria-label={hasDefaultBorder ? 'Transparent machen' : 'Farbig machen'}
          onClick={toggleBorderTransparency}
        >
          {createElement(hasDefaultBorder ? Opacity : SquareOutline, {
            'aria-hidden': true,
          })}
        </Button>

        <Button
          title={data.linkUrl ? 'Link entfernen' : 'Text verlinken'}
          shape="icon"
          variant="ghost"
          type="button"
          aria-label={data.linkUrl ? 'Link entfernen' : 'Text verlinken'}
          className={twMerge(
            'transition-all',
            data.linkUrl && 'bg-black text-white',
          )}
          onClick={onToggleLink}
        >
          {createElement(data.linkUrl ? LinkSlashIcon : LinkIcon, {
            'aria-hidden': true,
          })}
        </Button>

        <Button
          title={textAlign.label}
          shape="icon"
          variant="ghost"
          type="button"
          aria-label={textAlign.label}
          className="transition-all"
          onClick={cycleTextAlign}
        >
          <textAlign.icon aria-hidden="true" />
        </Button>

        <Button
          title="Löschen"
          shape="icon"
          variant="ghost"
          type="button"
          aria-label="Löschen"
          onClick={onDelete}
        >
          <TrashIcon aria-hidden="true" />
        </Button>
      </FlowToolbar>
    </NodeToolbar>
  )
}

function CustomNodeComponent(flowProps: NodeProps<CustomNodeType>) {
  const { data, id: thisNodeId, width, height, selected } = flowProps
  const flow = useReactFlow()
  const isEditable = !useReadOnly()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [localLabel, setLocalLabel] = useState(data.label)
  const updateNodeData = useUpdateNodeData<CustomNodeData>(thisNodeId)

  const isExternalLink = useMemo(() => {
    if (!data.linkUrl) return false
    try {
      const linkDomain = new URL(data.linkUrl).hostname
      const currentDomain = window.location.hostname
      return linkDomain !== currentDomain
    } catch {
      return false
    }
  }, [data.linkUrl])

  function handleLabelChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    event.stopPropagation()
    setLocalLabel(event.target.value)
  }

  function handleLabelSubmit() {
    updateNodeData({ label: localLabel })
    textAreaRef.current?.blur()
    setIsEditing(false)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleLabelSubmit()
    }
  }

  function handleLinkClick() {
    if (data.linkUrl) {
      updateNodeData({ linkUrl: null })
    } else {
      const url = prompt('Bitte geben Sie die URL ein')
      if (url) {
        updateNodeData({ linkUrl: url })
      }
    }
  }

  useEffect(() => {
    setLocalLabel(data.label)
  }, [data.label])

  const alignValue = TEXT_ALIGN[data.textAlign || 'center'].value

  const label = (
    <p
      className="custom-node-leaf"
      style={{ justifyContent: alignValue, textAlign: alignValue }}
    >
      {data.label}
    </p>
  )

  return (
    <div
      className="h-full w-full border-[1.5px] text-center"
      style={{
        backgroundColor:
          !data.borderColor || data.borderColor === DEFAULT_BORDER_COLOR
            ? data.backgroundColor || DEFAULT_NODE_BACKGROUND
            : 'transparent',
        width: width || '100%',
        height: height || '100%',
        borderColor: data.borderColor || DEFAULT_BORDER_COLOR,
      }}
    >
      <CustomNodeToolbar
        data={data}
        isVisible={isEditable && selected}
        updateNodeData={updateNodeData}
        onDelete={() => flow.deleteElements({ nodes: [{ id: thisNodeId }] })}
        onToggleLink={handleLinkClick}
      />

      <NodeResizer isVisible={isEditable && selected} />

      <NodeHandles isVisible={isEditable} />

      <div
        className="relative flex h-full w-full items-center justify-center"
        style={{
          color: highContrastText(
            data.backgroundColor || DEFAULT_NODE_BACKGROUND,
          ),
        }}
      >
        {isEditable ? (
          <textarea
            disabled={!isEditable}
            ref={textAreaRef}
            onChange={handleLabelChange}
            style={{
              textDecoration: data.linkUrl ? 'underline' : 'none',
              textAlign: alignValue,
            }}
            value={isEditing ? localLabel : data.label}
            onFocus={() => setIsEditing(true)}
            onBlur={handleLabelSubmit}
            onKeyDown={handleKeyDown}
            className="custom-node-leaf cursor-grab active:cursor-grabbing"
            rows={1}
          />
        ) : data.linkUrl ? (
          <a
            href={data.linkUrl}
            target={isExternalLink ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="h-full w-full cursor-pointer underline transition-colors hover:bg-black/10"
          >
            {label}
          </a>
        ) : (
          label
        )}
      </div>
    </div>
  )
}

export const CustomNode = memo(CustomNodeComponent)
